/**
 * FloatingRecorderWidget
 * A minimal floating widget that shows when recording is active or processing.
 * Includes mid-call ticket update controls.
 */

import { useState, useRef, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Mic, MicOff, Square, Pause, Play, Minimize2, Maximize2, X, Phone, Loader2, Upload, FileText, Send } from 'lucide-react'
import { useCallRecordingContext, formatDuration } from '@/contexts/CallRecordingContext'
import { useRecordingStatus, useRecordingFlow } from '@/hooks/useCallRecordings'
import { useInterimUpdates, type AutoUpdateInterval } from '@/hooks/useInterimUpdates'
import { getAudioFormat } from '@/hooks/useCallRecording'
import { cn } from '@/lib/utils'

interface FloatingRecorderWidgetProps {
  onProcessingComplete?: () => void
}

function getTimeSince(date: Date | null): string {
  if (!date) return ''
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000)
  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  return `${minutes}m ago`
}

export function FloatingRecorderWidget({ onProcessingComplete }: FloatingRecorderWidgetProps) {
  const {
    isRecording, isPaused, duration, audioLevel, sysId, table, recordDescription, status, recordingId, audioBlob, whisperBlob,
    pauseRecording, resumeRecording, stopRecording, cancelRecording, setStatus, setRecordingId, setError,
  } = useCallRecordingContext()

  const interim = useInterimUpdates(sysId, table)

  const [isMinimized, setIsMinimized] = useState(false)
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)
  const [uploadStarted, setUploadStarted] = useState(false)
  const dragRef = useRef<{ startX: number; startY: number; initialX: number; initialY: number } | null>(null)
  const widgetRef = useRef<HTMLDivElement>(null)

  // Force re-render for "time since" display
  const [, setTick] = useState(0)
  useEffect(() => {
    if (!interim.lastUpdateTime) return
    const id = setInterval(() => setTick(t => t + 1), 30000)
    return () => clearInterval(id)
  }, [interim.lastUpdateTime])

  const { uploadAndProcessMultipart } = useRecordingFlow()

  useEffect(() => {
    if (audioBlob && status === 'uploading' && !uploadStarted && !recordingId) {
      setUploadStarted(true)
      handleUpload()
    }
  }, [audioBlob, status, uploadStarted, recordingId])

  useEffect(() => { if (status === 'idle') setUploadStarted(false) }, [status])

  const handleUpload = async () => {
    if (!audioBlob) return
    try {
      const format = getAudioFormat(audioBlob.type)
      const id = await uploadAndProcessMultipart({
        audioBlob,
        whisperBlob: whisperBlob ?? undefined,
        audioFormat: format,
        durationSeconds: duration,
        sysId: sysId ?? undefined,
        table: table ?? undefined,
      })
      setRecordingId(id)
      setStatus('processing')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed')
      setStatus('error')
    }
  }

  const { data: statusData } = useRecordingStatus(
    status === 'processing' && recordingId ? recordingId : null,
    { pollingInterval: 2000 }
  )

  useEffect(() => {
    if (statusData?.status === 'completed') { setStatus('complete'); onProcessingComplete?.() }
    else if (statusData?.status === 'failed') { setError(statusData.errorMessage || 'Processing failed'); setStatus('error') }
  }, [statusData, setStatus, setError, onProcessingComplete])

  useEffect(() => {
    if (typeof window !== 'undefined') setPosition({ x: window.innerWidth - 300, y: window.innerHeight - 120 })
  }, [])

  useEffect(() => {
    if (!isDragging) return
    const onMouseMove = (e: MouseEvent) => {
      if (!dragRef.current) return
      setPosition({
        x: Math.max(0, Math.min(window.innerWidth - 300, dragRef.current.initialX + (e.clientX - dragRef.current.startX))),
        y: Math.max(0, Math.min(window.innerHeight - 100, dragRef.current.initialY + (e.clientY - dragRef.current.startY))),
      })
    }
    const onMouseUp = () => { setIsDragging(false); dragRef.current = null }
    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('mouseup', onMouseUp)
    return () => { window.removeEventListener('mousemove', onMouseMove); window.removeEventListener('mouseup', onMouseUp) }
  }, [isDragging])

  const shouldShow = isRecording || status === 'recording' || status === 'uploading' || status === 'processing'
  if (!shouldShow) return null

  const isProcessingPhase = status === 'uploading' || status === 'processing'
  const processingProgress = statusData?.progress || (status === 'uploading' ? 20 : 40)
  const processingLabel = status === 'uploading' ? 'Uploading...' : statusData?.status === 'transcribing' ? 'Transcribing...' : statusData?.status === 'generating_notes' ? 'Generating notes...' : 'Processing...'

  const handleMouseDown = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('button') || (e.target as HTMLElement).closest('select')) return
    setIsDragging(true)
    dragRef.current = { startX: e.clientX, startY: e.clientY, initialX: position.x, initialY: position.y }
  }

  const handleStopRecording = async () => { await stopRecording() }
  const handleCancel = () => { interim.resetInterimState(); cancelRecording() }

  if (isMinimized) {
    return (
      <div ref={widgetRef} style={{ left: position.x, top: position.y }} className="fixed z-50 cursor-move" onMouseDown={handleMouseDown}>
        <div className={cn('flex items-center gap-2 px-3 py-2 rounded-full shadow-lg', isProcessingPhase ? 'bg-blue-500' : isPaused ? 'bg-yellow-500' : 'bg-red-500')}>
          {isProcessingPhase ? <Loader2 className="h-3 w-3 text-white animate-spin" /> : <span className={cn('w-3 h-3 rounded-full', isPaused ? 'bg-yellow-200' : 'bg-white animate-pulse')} />}
          <span className="text-white font-mono text-sm font-medium">{isProcessingPhase ? processingLabel : formatDuration(duration)}</span>
          {interim.isUpdating && <Loader2 className="h-3 w-3 text-white animate-spin" />}
          <Button variant="ghost" size="icon" className="h-6 w-6 text-white hover:bg-white/20" onClick={(e) => { e.stopPropagation(); setIsMinimized(false) }}>
            <Maximize2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div ref={widgetRef} style={{ left: position.x, top: position.y }} className={cn('fixed z-50 w-[280px] bg-white dark:bg-slate-800 rounded-lg shadow-xl border border-gray-200 dark:border-slate-600 overflow-hidden', isDragging && 'cursor-grabbing')} onMouseDown={handleMouseDown}>
      <div className={cn('flex items-center justify-between px-3 py-2 cursor-move', isProcessingPhase ? 'bg-blue-500' : isPaused ? 'bg-yellow-500' : 'bg-red-500')}>
        <div className="flex items-center gap-2">
          {isProcessingPhase ? <Loader2 className="h-4 w-4 text-white animate-spin" /> : <Phone className="h-4 w-4 text-white" />}
          <span className="text-white text-sm font-medium">{isProcessingPhase ? processingLabel : isPaused ? 'Paused' : 'Recording'}</span>
        </div>
        <Button variant="ghost" size="icon" className="h-6 w-6 text-white hover:bg-white/20" onClick={(e) => { e.stopPropagation(); setIsMinimized(true) }} title="Minimize">
          <Minimize2 className="h-3.5 w-3.5" />
        </Button>
      </div>

      <div className="p-3 space-y-3">
        {sysId && <div className="text-xs text-gray-500 dark:text-slate-400 truncate">{recordDescription || sysId}</div>}

        {isProcessingPhase ? (
          <>
            <div className="flex flex-col items-center gap-3 py-2">
              <div className="flex items-center gap-2">
                {status === 'uploading' && <Upload className="h-5 w-5 text-blue-500" />}
                {status === 'processing' && <FileText className="h-5 w-5 text-blue-500" />}
              </div>
              <p className="text-sm text-gray-600 dark:text-slate-300 text-center">
                {status === 'uploading' && 'Uploading audio...'}
                {status === 'processing' && statusData?.status === 'transcribing' && 'Transcribing audio...'}
                {status === 'processing' && statusData?.status === 'generating_notes' && 'Generating AI notes...'}
                {status === 'processing' && !statusData?.status && 'Processing...'}
              </p>
            </div>
            <div className="h-2 bg-gray-200 dark:bg-slate-600 rounded-full overflow-hidden">
              <div className="h-full bg-blue-500 rounded-full transition-all duration-300" style={{ width: `${processingProgress}%` }} />
            </div>
            <p className="text-xs text-center text-gray-500 dark:text-slate-400">Recording: {formatDuration(duration)}</p>
            <p className="text-xs text-center text-gray-400 dark:text-slate-500 italic">Continue working - we'll notify you when ready</p>
          </>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={cn('w-3 h-3 rounded-full', isPaused ? 'bg-yellow-500' : 'bg-red-500 animate-pulse')} />
                <span className="font-mono text-2xl font-semibold text-gray-900 dark:text-white">{formatDuration(duration)}</span>
              </div>
              <div className="flex items-center gap-1">
                {isPaused ? <MicOff className="h-4 w-4 text-yellow-500" /> : <Mic className={cn('h-4 w-4', audioLevel > 0.1 ? 'text-green-500' : 'text-gray-400')} />}
                <div className="w-16 h-2 bg-gray-200 dark:bg-slate-600 rounded-full overflow-hidden">
                  <div className={cn('h-full rounded-full transition-all duration-100', isPaused ? 'bg-yellow-500' : 'bg-green-500')} style={{ width: `${Math.min(100, audioLevel * 100)}%` }} />
                </div>
              </div>
            </div>

            {/* Main controls */}
            <div className="flex items-center justify-center gap-2">
              <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); isPaused ? resumeRecording() : pauseRecording() }} className="flex-1">
                {isPaused ? <><Play className="h-4 w-4 mr-1" />Resume</> : <><Pause className="h-4 w-4 mr-1" />Pause</>}
              </Button>
              <Button size="sm" onClick={(e) => { e.stopPropagation(); handleStopRecording() }} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white">
                <Square className="h-4 w-4 mr-1" />Stop
              </Button>
            </div>

            {/* Interim update controls - only when linked to a ticket */}
            {sysId && (
              <div className="space-y-1.5 pt-1 border-t border-gray-100 dark:border-slate-700">
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); interim.triggerUpdate() }}
                    disabled={interim.isUpdating || duration < 5}
                    className="flex-1 text-xs h-7"
                  >
                    {interim.isUpdating ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Send className="h-3 w-3 mr-1" />}
                    Update Ticket
                  </Button>
                  <select
                    value={interim.autoUpdateInterval}
                    onChange={(e) => { e.stopPropagation(); interim.setAutoUpdateInterval(Number(e.target.value) as AutoUpdateInterval) }}
                    className={cn(
                      'h-7 text-xs border rounded-md px-1.5 bg-white dark:bg-slate-900 text-gray-700 dark:text-slate-300 dark:border-slate-600 cursor-pointer',
                      interim.autoUpdateInterval > 0 && 'border-green-500 dark:border-green-500 text-green-700 dark:text-green-400'
                    )}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <option value={0}>Auto: Off</option>
                    <option value={5}>Auto: 5m</option>
                    <option value={15}>Auto: 15m</option>
                    <option value={30}>Auto: 30m</option>
                  </select>
                </div>
                {/* Status line */}
                {interim.isUpdating && (
                  <p className="text-[10px] text-blue-600 dark:text-blue-400 text-center">Updating ticket...</p>
                )}
                {!interim.isUpdating && interim.lastUpdateTime && (
                  <p className="text-[10px] text-green-600 dark:text-green-400 text-center">
                    Updated {getTimeSince(interim.lastUpdateTime)} (#{interim.lastUpdateNumber})
                  </p>
                )}
                {interim.error && !interim.isUpdating && (
                  <p className="text-[10px] text-red-500 dark:text-red-400 text-center truncate">{interim.error}</p>
                )}
              </div>
            )}

            <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleCancel() }} className="w-full text-gray-500 hover:text-red-500">
              <X className="h-4 w-4 mr-1" />Cancel Recording
            </Button>
          </>
        )}
      </div>
    </div>
  )
}
